import React, { FC, useCallback, useMemo } from 'react';
import GenericSearchDropdown from '../../GenericSearchDropDown';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import { Button, DatePicker, Form } from 'src/common';
import { useApiTenders } from 'src/models/procurements/Tenders/hooks';
import { Dayjs } from 'dayjs';
import dayjs from 'src/helpers/dayjs';
import { debounce } from 'lodash';
import { trackTendersFilterDate } from 'src/segment/events';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';

type FormState = {
  published?: [Dayjs, Dayjs];
  deadline?: [Dayjs, Dayjs];
};
const { RangePicker } = DatePicker;

const FilterDates: FC = () => {
  const { t } = useTranslation();
  const [{ vars, initVars, eventSource }, updateVars] = useApiTenders();

  const [form] = Form.useForm<FormState>();

  const initialValues: FormState = useMemo(
    () => ({
      published: vars.published ? (vars.published.map(date => dayjs(date)) as [Dayjs, Dayjs]) : undefined,
      deadline: vars.deadline ? (vars.deadline.map(date => dayjs(date)) as [Dayjs, Dayjs]) : undefined
    }),
    [vars.deadline, vars.published]
  );
  const onValuesChange = useMemo(
    () =>
      debounce((changedValues: Partial<FormState>) => {
        if (changedValues.published) {
          updateVars({
            published: [
              dayjs(changedValues.published[0]).utc().startOf('day').valueOf(),
              dayjs(changedValues.published[1]).utc().endOf('day').valueOf()
            ]
          });
          trackTendersFilterDate(eventSource, 'published');
        } else if (changedValues.deadline) {
          updateVars({
            deadline: [
              dayjs(changedValues.deadline[0]).utc().startOf('day').valueOf(),
              dayjs(changedValues.deadline[1]).utc().endOf('day').valueOf()
            ]
          });
          trackTendersFilterDate(eventSource, 'deadline');
        }
      }, 300),
    [eventSource, updateVars]
  );

  const isChanged = useMemo(() => !!vars.deadline || !!vars.published, [vars.deadline, vars.published]);

  const onClearDeadline = useCallback(() => {
    form.setFieldsValue({ deadline: undefined });
    updateVars({ deadline: initVars.deadline });
  }, [form, initVars.deadline, updateVars]);

  const onClearPublished = useCallback(() => {
    form.setFieldsValue({ published: undefined });
    updateVars({ published: initVars.published });
  }, [form, initVars.published, updateVars]);

  return (
    <GenericSearchDropdown
      label={t('Tenders.dates')}
      isChanged={isChanged}
      overlayClassName={styles.overlay}
      overlay={
        <Form onValuesChange={onValuesChange} form={form} initialValues={initialValues} className={styles.form}>
          <Form.Item name={'published'} label={t('Tenders.published')} className={styles.field}>
            <RangePicker
              className={styles.range}
              dropdownClassName={styles.rangeOverlay}
              separator={'-'}
              allowClear={false}
              getPopupContainer={triggerNode => triggerNode.parentNode as HTMLElement}
              renderExtraFooter={() => (
                <Button
                  type={'text'}
                  icon={<FontAwesomeIcon icon={faTimes} />}
                  onClick={onClearPublished}
                  className={styles.clearButton}
                  disabled={!form.getFieldValue('published')}
                >
                  {t('Common.Blocks.deleteDate')}
                </Button>
              )}
            />
          </Form.Item>
          <Form.Item name={'deadline'} label={t('Tenders.submissionDeadline')} className={styles.field}>
            <RangePicker
              className={styles.range}
              dropdownClassName={styles.rangeOverlay}
              separator={'-'}
              allowClear={false}
              getPopupContainer={triggerNode => triggerNode.parentNode as HTMLElement}
              renderExtraFooter={() => (
                <Button
                  type={'text'}
                  icon={<FontAwesomeIcon icon={faTimes} />}
                  onClick={onClearDeadline}
                  className={styles.clearButton}
                  disabled={!form.getFieldValue('deadline')}
                >
                  {t('Common.Blocks.deleteDate')}
                </Button>
              )}
            />
          </Form.Item>
        </Form>
      }
    />
  );
};

export default FilterDates;
